import React from 'react';
import { camelCase, isDefined, isEmpty, mapHtmlPropsToReact, RESERVATION_SEAT_TYPE, silenceError, valOrThrow } from '@nfctron/nexus-core';
import { useLogger } from '@nfctron/nexus-react';
import type { ElementNode, Node } from 'svg-parser';

import { Crowd } from '../../Crowd/Crowd';
import { Seat } from '../../Seat/Seat';
import type { SectorMapTypes } from '../SectorMap.types';

interface SvgRendererProps {
	root: ElementNode;
	seatMap: Map<string, SectorMapTypes.SeatData>;
	crowdMap: Map<string, SectorMapTypes.CrowdData>;
	categoryMap: Map<string, SectorMapTypes.Props['categories'][number]>;
	onCrowdSelect: SectorMapTypes.Props['onCrowdSelect'];
	onCrowdUnselect: SectorMapTypes.Props['onCrowdUnselect'];
	getCrowdProps: SectorMapTypes.Props['getCrowdProps'];
	onSeatSelect: SectorMapTypes.Props['onSeatSelect'];
	onSeatUnselect: SectorMapTypes.Props['onSeatUnselect'];
	getSeatProps: SectorMapTypes.Props['getSeatProps'];
}

const transformSvgProps = (props: Record<string, unknown>) => {
	const transformed: Record<string, unknown> = {};
	const internalProps = ['dataSeatRow', 'dataPlace', 'dataFullName', 'dataFullNameTemplate', 'dataLabelTemplate', 'dataSeatTypeId', 'dataLabel'];

	for (const [key, value] of Object.entries(props)) {
		/** skip internal props */
		if (internalProps.includes(key)) continue;

		/** transform kebab-case to camelCase for SVG properties */
		const camelKey = camelCase(key);
		transformed[camelKey] = value;
	}

	return transformed;
};

export const SvgRenderer = React.memo(
	({
		root,
		seatMap,
		crowdMap,
		categoryMap,
		onCrowdSelect,
		onCrowdUnselect,
		getCrowdProps,
		onSeatSelect,
		onSeatUnselect,
		getSeatProps,
	}: SvgRendererProps) => {
		const { loggerLow } = useLogger('SvgRenderer', { medium: true, high: true });

		/** creates a possibly unique key for the svg element */
		const createSvgElKey = React.useCallback(
			(key: number, id?: string, el?: string) => ['SectorMap', id ?? 'root', el, key].filter(Boolean).join('-'),
			[],
		);

		/** node child renderer callback */
		const renderNodeChild = React.useCallback(
			(childNode: Node | string, key: number): React.ReactNode => {
				/** string node, return none */
				if (typeof childNode === 'string') return null;

				/** render node function */
				const renderNode = (node: Node): React.ReactNode => {
					/** string node, return none */
					if (node.type === 'text') return null;

					/** map node properties */
					node.properties = isDefined(node.properties) ? mapHtmlPropsToReact(node.properties) : {};

					/** render node depending on the svg tag */
					switch (node.tagName) {
						/** svg path */
						case 'rect':
						case 'path': {
							const idAttr: string | undefined = node.properties?.id?.toString();

							/** crowd svg path */
							if (!isEmpty(idAttr) && idAttr.startsWith('crowd:')) {
								const crowd = crowdMap.get(idAttr.replace('crowd:', ''));
								const category = crowd ? categoryMap.get(crowd.categoryId) : undefined;

								if (isDefined(node.properties) && isDefined(crowd) && isDefined(category)) {
									return (
										<Crowd
											key={createSvgElKey(key, crowd.crowdId ?? idAttr, 'Crowd')}
											tag={node.tagName ?? 'path'}
											{...node.properties}
											onCrowdSelect={onCrowdSelect}
											onCrowdUnselect={onCrowdUnselect}
											getCrowdProps={getCrowdProps}
											crowd={{ ...crowd, category }}
										>
											{node.children.map((child, index) => renderNodeChild(child, key + index))}
										</Crowd>
									);
								}
							}

							/** ordinary rect/path svg */
							const Tag = node.tagName;
							return React.createElement(
								Tag,
								{
									key: createSvgElKey(key, idAttr, `svg-${node.tagName}`),
									...transformSvgProps(node.properties),
								},
								node.children.map((child, index) => renderNodeChild(child, key + index)),
							);
						}

						/** svg group */
						case 'g': {
							const idAttr: string | undefined = node.properties?.id?.toString();
							const noSeatFoundProps = {
								fill: '#e4e4e7',
								strokeWidth: 10,
								stroke: '#d4d4d8',
							} as const;

							/** section groups with seats inside */
							if (idAttr?.includes('section')) {
								/** get section attributes */
								const sectionAttrs = silenceError(
									() => ({
										id: valOrThrow(node.properties?.id, 'id is missing'),
										dataSeatRow: valOrThrow(node.properties?.['data-seat-row'] ?? node.properties?.dataSeatRow, 'dataSeatRow is missing'),
										dataFullNameTemplate: (node.properties?.['data-full-name-template'] ?? node.properties?.dataFullNameTemplate) as
											| string
											| undefined,
										dataLabelTemplate: (node.properties?.['data-label-template'] ?? node.properties?.dataLabelTemplate) as string | undefined,
										dataSeatTypeId: node.properties?.['data-seat-type-id'] ?? node.properties?.dataSeatTypeId ?? RESERVATION_SEAT_TYPE.CIRCLE_50CM,
									}),
									null,
								);

								/** invalid section configuration */
								if (!isDefined(sectionAttrs)) {
									loggerLow(`Section attributes not found: ${idAttr}`);
									return null;
								}

								/** render seats */
								const seatElements = node.children.map((child, index) => {
									if (typeof child === 'string' || child.type === 'text') return null;

									/** get seat attributes */
									const seatAttrs = silenceError(() => {
										const dataPlace = valOrThrow(child.properties?.['data-place'] ?? child.properties?.dataPlace, 'dataPlace is missing');
										const dataLabel = child.properties?.['data-label'] ?? child.properties?.dataLabel;
										const dataSeatRow = child.properties?.['data-seat-row'] ?? child.properties?.dataSeatRow ?? sectionAttrs.dataSeatRow;
										return {
											dataPlace,
											dataLabel,
											dataSeatRow,
											dataFullName:
												child.properties?.['data-full-name'] ??
												child.properties?.dataFullName ??
												sectionAttrs.dataFullNameTemplate
													?.replace('{{place}}', dataPlace?.toString() ?? '')
													?.replace('{{seatRow}}', dataSeatRow?.toString() ?? '')
													?.replace('{{label}}', dataLabel?.toString() ?? ''),
											dataSeatTypeId: child.properties?.['data-seat-type-id'] ?? child.properties?.dataSeatTypeId ?? sectionAttrs.dataSeatTypeId,
										};
									}, null);

									/** find seat and category */
									const seat = seatAttrs ? seatMap.get(`${seatAttrs.dataSeatRow}-${seatAttrs.dataPlace}`) : undefined;
									const category = seat ? categoryMap.get(seat.categoryId) : undefined;

									if (!isDefined(seat) || !isDefined(category)) {
										loggerLow(`Seat configuration not found: ${seatAttrs?.dataSeatRow}+${seatAttrs?.dataPlace}`);
										return React.createElement(child.tagName!, {
											...child.properties,
											...noSeatFoundProps,
											key: createSvgElKey(key + index, undefined, `no-seat-${index}`),
										});
									}

									return (
										<Seat
											key={createSvgElKey(key + index, seat.seatId, 'Seat')}
											onSeatSelect={onSeatSelect}
											onSeatUnselect={onSeatUnselect}
											getSeatProps={getSeatProps}
											seat={{ ...seat, category }}
										/>
									);
								});

								return React.createElement(
									'g',
									{
										key: createSvgElKey(key, idAttr, 'section'),
										...transformSvgProps(node.properties),
									},
									seatElements,
								);
							}

							/** ordinary svg group */
							return React.createElement(
								'g',
								{
									key: createSvgElKey(key, idAttr, 'g'),
									...transformSvgProps(node.properties),
								},
								node.children.map((child, index) => renderNodeChild(child, key + index)),
							);
						}

						/** any other svg element */
						default: {
							if (isEmpty(node.tagName)) return null;
							return React.createElement(
								node.tagName,
								{
									key: createSvgElKey(key, node.properties?.id?.toString(), `svg-${node.tagName}`),
									...transformSvgProps(node.properties),
								},
								node.children.map((child, index) => renderNodeChild(child, key + index)),
							);
						}
					}
				};

				return renderNode(childNode);
			},
			[
				createSvgElKey,
				crowdMap,
				categoryMap,
				seatMap,
				onCrowdSelect,
				onCrowdUnselect,
				getCrowdProps,
				onSeatSelect,
				onSeatUnselect,
				getSeatProps,
				loggerLow,
			],
		);

		return <React.Fragment>{root.children.map(renderNodeChild)}</React.Fragment>;
	},
);

SvgRenderer.displayName = 'SvgRenderer';
